import { useCallback, useEffect, useRef, useState } from 'react';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatCOP, formatDate } from '@/lib/format';
import { useSEO } from '@/lib/useSEO';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { PayoutSafeReconcileButton } from '@/components/PayoutSafeReconcileButton';
import { PlayCircle, RefreshCw, Eye, ShieldAlert, CheckCircle2, Clock, Send, Ban, Undo2, AlertTriangle, History } from 'lucide-react';

const STATUS_META = {
  pending_delivery: { label: 'Pendiente entrega', cls: 'bg-slate-100 text-slate-700 border-slate-200', icon: Clock },
  ready:            { label: 'Listo',              cls: 'bg-blue-100 text-blue-800 border-blue-200',  icon: Clock },
  scheduled:        { label: 'Programado',         cls: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: Send },
  sent:             { label: 'Enviado',            cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Send },
  completed:        { label: 'Completado',         cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  held:             { label: 'Bloqueado',          cls: 'bg-rose-100 text-rose-800 border-rose-200', icon: ShieldAlert },
  refunded:         { label: 'Reembolsado',        cls: 'bg-slate-100 text-slate-700 border-slate-200', icon: Undo2 },
  failed:           { label: 'Falló',              cls: 'bg-rose-100 text-rose-800 border-rose-200', icon: Ban },
};

export default function AdminPayouts() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('all');
  const [sellerId, setSellerId] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sweepRunning, setSweepRunning] = useState(false);
  const [detail, setDetail] = useState(null); // full payout object
  const [reconciliation, setReconciliation] = useState(null);
  const LIMIT = 20;

  useSEO({
    title: 'Retiros a vendedores | Admin',
    description: 'Panel de payouts automáticos, timeline, bloqueadores y reconciliación con Wompi.',
    canonical: '/admin/payouts',
  });

  // El filtro por vendedor se aplica al pulsar «Aplicar»/Enter, no en cada tecla.
  const sellerIdRef = useRef(sellerId);
  sellerIdRef.current = sellerId;
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, skip: page * LIMIT };
      if (status !== 'all') params.status = status;
      const sid = sellerIdRef.current.trim();
      if (sid) params.seller_id = sid;
      const { data } = await api.get('/admin/payouts', { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [status, page]);

  const loadReconciliation = async () => {
    try {
      const { data } = await api.get('/admin/finance/reconciliation');
      setReconciliation(data);
    } catch {
      setReconciliation(null);
    }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadReconciliation(); }, []);

  const runSweep = async (force) => {
    setSweepRunning(true);
    try {
      const { data } = await api.post('/admin/payouts/sweep', null, { params: { force } });
      toast.success(`Sweep OK · +${data.evaluate.scheduled} programados, +${data.dispatch.sent} enviados, +${data.poll.completed} completados`);
      load();
      loadReconciliation();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setSweepRunning(false);
    }
  };

  const openDetail = async (payout_id) => {
    try {
      const { data } = await api.get(`/admin/payouts/${payout_id}`);
      setDetail(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Admin · Finanzas</div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Retiros a vendedores</h1>
            <p className="text-sm text-slate-500 mt-1">Payouts automáticos · Wompi Transfers · Reconciliación</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/admin/payouts/runs"
              data-testid="admin-payouts-runs-link"
              className="inline-flex items-center gap-1 rounded-none border border-slate-300 hover:bg-slate-100 px-3 py-2 text-sm font-semibold"
            >
              <History className="h-4 w-4" /> Ejecuciones
            </Link>
            <Button
              data-testid="admin-payouts-sweep-btn"
              onClick={() => runSweep(false)}
              disabled={sweepRunning}
              className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
            >
              <PlayCircle className="mr-1 h-4 w-4" /> {sweepRunning ? 'Corriendo…' : 'Ejecutar sweep'}
            </Button>
            <Button
              data-testid="admin-payouts-sweep-force-btn"
              onClick={() => runSweep(true)}
              disabled={sweepRunning}
              variant="outline"
              className="rounded-none"
            >
              <PlayCircle className="mr-1 h-4 w-4" /> Forzado (bypass período)
            </Button>
          </div>
        </div>

        {/* Reconciliation banner */}
        {reconciliation && (
          <div
            data-testid="admin-payouts-reconciliation"
            className={`mt-6 border p-4 ${reconciliation.reconciliation.in_sync ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`}
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                {reconciliation.reconciliation.in_sync
                  ? <CheckCircle2 className="h-5 w-5 text-emerald-700" />
                  : <AlertTriangle className="h-5 w-5 text-rose-700" />}
                <span className="font-bold text-sm">
                  {reconciliation.reconciliation.in_sync ? 'Ledger y Wompi conciliados' : `Diferencia detectada: ${formatCOP(reconciliation.reconciliation.diff)}`}
                </span>
              </div>
              <div className="flex gap-4 text-xs">
                <span><b>Payouts (sent+completed):</b> {formatCOP(reconciliation.reconciliation.sent_payouts_total)}</span>
                <span><b>Wompi (sent+completed):</b> {formatCOP(reconciliation.reconciliation.wompi_sent_completed_total)}</span>
                <button onClick={loadReconciliation} className="text-blue-700 underline"><RefreshCw className="inline h-3 w-3 mr-1" />Recargar</button>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
              {Object.entries(reconciliation.seller_payouts.by_status).map(([s, v]) => {
                const meta = STATUS_META[s] || { label: s, cls: 'bg-slate-100' };
                return (
                  <div key={s} className={`border p-2 ${meta.cls}`}>
                    <div className="font-bold text-[10px] uppercase">{meta.label}</div>
                    <div className="text-slate-900 font-mono">{v.count} · {formatCOP(v.net)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="mt-6 flex flex-wrap gap-3 items-end">
          <div className="min-w-[180px]">
            <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Estado</label>
            <Select value={status} onValueChange={(v) => { setStatus(v); setPage(0); }}>
              <SelectTrigger data-testid="admin-payouts-status-filter" className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.keys(STATUS_META).map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[240px] flex-1">
            <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Vendedor (user_id)</label>
            <Input
              data-testid="admin-payouts-seller-filter"
              placeholder="Filtra por seller_id"
              value={sellerId}
              onChange={(e) => setSellerId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (setPage(0), load())}
              className="rounded-none"
            />
          </div>
          <Button data-testid="admin-payouts-apply-filters" onClick={() => { setPage(0); load(); }} className="rounded-none bg-slate-900 hover:bg-slate-800 text-white h-10">
            Aplicar
          </Button>
        </div>

        {/* Table */}
        <div className="mt-4 border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2">Payout</th>
                <th className="px-3 py-2">Vendedor</th>
                <th className="px-3 py-2">Pedido</th>
                <th className="px-3 py-2 text-right">Bruto</th>
                <th className="px-3 py-2 text-right">Comisión</th>
                <th className="px-3 py-2 text-right">Neto</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-testid="admin-payouts-table">
              {loading && (<tr><td colSpan={9} className="p-8 text-center text-slate-500">Cargando…</td></tr>)}
              {!loading && items.length === 0 && (<tr><td colSpan={9} className="p-8 text-center text-slate-500">Sin resultados</td></tr>)}
              {items.map((p) => {
                const meta = STATUS_META[p.status] || { label: p.status, cls: 'bg-slate-100' };
                return (
                  <tr key={p.payout_id} className="hover:bg-slate-50" data-testid={`admin-payout-row-${p.payout_id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{p.payout_id.slice(0, 12)}…</td>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{p.seller_name || '—'}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{p.seller_id.slice(0, 12)}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{p.order_id.slice(0, 12)}…</td>
                    <td className="px-3 py-2 text-right font-mono">{formatCOP(p.gross)}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-500">{formatCOP(p.commission)}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{formatCOP(p.net)}</td>
                    <td className="px-3 py-2">
                      <Badge className={`rounded-none border ${meta.cls}`}>{meta.label}</Badge>
                      {p.status === 'held' && p.held_reason && (
                        <div className="text-[10px] text-rose-600 mt-1">🚫 {p.held_reason}</div>
                      )}
                      {p.status === 'held' && p.release_review && (
                        <div className={`text-[10px] mt-1 ${p.release_review.eligible ? 'text-emerald-700' : 'text-amber-700'}`} data-testid={`admin-payout-release-review-${p.payout_id}`}>
                          {p.release_review.eligible ? 'Revalidado: listo para aprobación del admin' : `Revalidado: sigue bloqueado (${p.release_review.blocker})`}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{formatDate(p.created_at)}</td>
                    <td className="px-3 py-2">
                      <button
                        data-testid={`admin-payout-view-${p.payout_id}`}
                        onClick={() => openDetail(p.payout_id)}
                        className="text-blue-700 hover:underline text-xs inline-flex items-center gap-1"
                      >
                        <Eye className="h-3 w-3" /> Timeline
                      </button>
                      {['held', 'failed'].includes(p.status) && (
                        <div><PayoutSafeReconcileButton payoutId={p.payout_id} onDone={load} /></div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-2" data-testid="admin-payouts-pagination">
            <Button variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="rounded-none">Anterior</Button>
            <span className="text-sm text-slate-600 px-3">{page + 1} / {totalPages}</span>
            <Button variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} className="rounded-none">Siguiente</Button>
          </div>
        )}

        {/* Detail dialog */}
        <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="rounded-none bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Timeline del payout</DialogTitle></DialogHeader>
            {detail && (
              <div className="space-y-4 text-slate-900">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><div className="text-[10px] font-bold uppercase text-slate-500">Payout</div><div className="font-mono">{detail.payout.payout_id}</div></div>
                  <div><div className="text-[10px] font-bold uppercase text-slate-500">Estado</div><Badge className={`rounded-none border ${(STATUS_META[detail.payout.status] || {}).cls}`}>{(STATUS_META[detail.payout.status] || {}).label || detail.payout.status}</Badge></div>
                  <div><div className="text-[10px] font-bold uppercase text-slate-500">Vendedor</div><div>{detail.seller.name} <span className="text-slate-400 text-xs">({detail.seller.email})</span></div></div>
                  <div><div className="text-[10px] font-bold uppercase text-slate-500">Pedido</div><div className="font-mono text-xs">{detail.payout.order_id}</div></div>
                  <div><div className="text-[10px] font-bold uppercase text-slate-500">Bruto</div><div className="font-mono">{formatCOP(detail.payout.gross)}</div></div>
                  <div><div className="text-[10px] font-bold uppercase text-slate-500">Comisión AutoMarket</div><div className="font-mono">{formatCOP(detail.payout.commission)}</div></div>
                  <div><div className="text-[10px] font-bold uppercase text-slate-500">Neto vendedor</div><div className="font-mono font-bold">{formatCOP(detail.payout.net)}</div></div>
                  {detail.payout.gateway_transaction_id && (
                    <div className="col-span-2"><div className="text-[10px] font-bold uppercase text-slate-500">Wompi tx</div><div className="font-mono text-xs break-all">{detail.payout.gateway_transaction_id}</div></div>
                  )}
                </div>
                {detail.wompi_transfer && (
                  <div className="border border-slate-200 bg-slate-50 p-3 text-xs">
                    <div className="font-bold uppercase text-[10px] tracking-wider text-slate-500 mb-1">Wompi Transfer (simulated)</div>
                    <div>Cuenta enmascarada: <span className="font-mono">{detail.wompi_transfer.account_masked}</span></div>
                    <div>Banco: {detail.wompi_transfer.bank || '—'}</div>
                    <div>Enviado: {detail.wompi_transfer.sent_at ? formatDate(detail.wompi_transfer.sent_at) : '—'}</div>
                    <div>Completado: {detail.wompi_transfer.completed_at ? formatDate(detail.wompi_transfer.completed_at) : '—'}</div>
                  </div>
                )}
                <div>
                  <div className="text-[10px] font-bold uppercase text-slate-500 mb-2">Historial</div>
                  <ol className="border-l-2 border-slate-200 pl-4 space-y-3">
                    {(detail.payout.history || []).map((h, i) => (
                      <li key={i} className="relative">
                        <span className="absolute -left-[19px] top-1 h-3 w-3 rounded-full bg-slate-900" />
                        <div className="text-xs">
                          <span className="font-bold">{(STATUS_META[h.status] || {}).label || h.status}</span>
                          <span className="ml-2 text-slate-500">{formatDate(h.at)}</span>
                          {h.actor && <span className="ml-2 text-slate-400">· {h.actor}</span>}
                        </div>
                        {h.note && <div className="text-xs text-slate-600">{h.note}</div>}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
